import type {ISOString, Nullable} from './general.js';
import type {ByType} from './journey/schedule.js';
import type {Option} from '#lit/components/base/multiselect_async/index.js';
import type {MappedMatchField} from '#types/journeys.js';
import type {
  AudienceFrequencyEnum,
  AudienceScheduleEndTypeEnum,
  AudienceScheduleStartTypeEnum,
} from './journey/audience_frequency.js';
import type {Weekday} from '#lit/common/constants/dates.js';
import {
  QualtricsListType,
  MarketoAudienceType,
  SailthruApiType,
} from '#lit/components/audiences/audience_export_form/constants.js';

export type ExtraConfig = {
  distribution_manager_ids: string[];
};

export type ExportSettings = {
  table_name?: string;
  extra_config?: ExtraConfig;
};

export interface AudienceExport {
  id: number;
  audience_id: number;
  audience_name: string;
  campaign_type: string;
  sync_frequency: string;
  ends_at: string;
  created_at: string;
  updated_at: string;
  is_enabled: boolean;
  last_snapshot_attempt: string;
  schedule: Nullable<AudienceExportSchedule>;
  active: boolean;
  snapshotable: boolean;
  settings: ExportSettings;
  platform_type: string;
  destination_connection_id: number;
  ingest_source_type: string;
  ingest_status: IngestStatus;
}

export const IngestStatus = {
  INGESTING: 'ingesting',
  INGESTED: 'ingested',
  NOT_INGESTED: 'not_ingested',
  NOT_SUPPORTED: 'not_supported',
} as const;

export type IngestStatus = (typeof IngestStatus)[keyof typeof IngestStatus];

/**
 * Old data structure for additional field.
 * @deprecated
 */
export interface DeprecatedAdditionalField {
  index: number;
  alias: string;
  value: string;
}

export type AdditionalFieldRecord = {
  computed?: boolean;
  systemGenerated?: boolean;
  labelTag?: string;
};

export interface IAdditionalField {
  id: string;
  // field_alias
  alias: string;
  // field_name
  value: string;
  displayName: string;
  order: number;
  type?: string;
  record?: AdditionalFieldRecord;
}

export interface IAdditionalFieldDTO {
  field_name: string;
  type: string;
  display_name: string;
  field_alias: string;
  order: number;
  record?: AdditionalFieldRecord;
}

export type AdditionalFieldResponseValue = Record<string, IAdditionalFieldDTO>;
export type AdditionalFieldResponse = AdditionalFieldResponseValue[];

export type AdditionalFieldMap = Map<string, IAdditionalField>;

export interface ExportActivitiesResponse {
  export_activities: ExportActivity[];
}

export interface ExportActivity {
  ack_id: string;
  uri: string;
  platform: string;
  file: string;
  endpoint_id: string;
  audience_id: string;
  match_fields: string[];
  batch_size: number;
  organization_id: string;
  organization_name: string;
  internal_audience_id: string;
  batch_id: string;
  organization_config: OrganizationConfig;
  snapshot_time: number;
  export_timestamp: string;
  error_file_uri: string;
  audience_name: string;
  warehouse_database_type: string;
  supports_state: boolean;
  export_state: ExportState;
  time: string;
  successfull_exports: number;
  failed_exports: number;
  destination_name: string;
  export_run_id: ExportRunId;
}

export interface ExportActivitySummary {
  id: number;
  audience_id: number;
  audience_name: string;
  dataset_group_id: number;
  legacy: boolean;
  destination_id: number;
  destination_name: string;
  destination_icon: string;
  export_run_id: ExportRunId;
  platform: string;
  successful_add_count: number;
  successful_remove_count: number;
  failed_export_count: number;
  rejected_row_count: number;
  total_row_count: number;
  total_batch_count: number;
  completed_batch_count: number;
  successful_batch_count: number;
  failed_batch_count: number;
  export_state: ExportState;
  snapshotting_state: string;
  time: string;
}

export interface OrganizationConfig {
  id: number;
  name: string;
  identifier: string;
  features: OrganizationFeatures;
  settings: OrganizationSettings;
  datawarehouse: DataWarehouse;
  is_mlb_club: boolean;
}

export interface OrganizationFeatures {
  enable_dbt: boolean;
  enable_evaluation: boolean;
  enable_invites: boolean;
  enable_logging: boolean;
}

interface OrganizationSettings {
  legacy_audiences: boolean;
  snapshot_retention: number;
  external_log_run_logs: boolean;
  external_log_runs_metadata: boolean;
  external_log_audience_metadata: boolean;
  automatic_audience_report_refresh: boolean;
  snapshot_dataset: string;
}

interface DataWarehouse {
  credentials: string;
  project_id: string;
}

export interface ExportRunId {
  value: string;
  short_version: string;
  raw: string;
}

export enum ExportConfigFormField {
  PLATFORM_TYPE = 'platform_type',
  //mlb privacy settings
  LOSP = 'losp',
  END_USER_OPT_IN = 'end_user_opt_in',
  THIRD_PARTY_NAME = 'third_party_name',
  CLUB_SPONSOR_ADVERTISER_IO = 'club_sponsor_advertiser_io',
  OPT_IN_LANGUAGE_APPROVED_VETTED = 'opt_in_language_approved_vetted',
  CLUB_SPONSOR_ADVERTISER_DATA_SHARE = 'club_sponsor_advertiser_data_share',
  INFO_COLLECTED_VIA_MLB_PRIVACY_POLICY = 'info_collected_via_mlb_privacy_policy',
  CAMPAIGN_TYPE = 'campaign_type',
  SYNC_FREQUENCY = 'syncFrequency',
  MAPPED_MATCH_FIELDS = 'mappedMatchFields',
  ENHANCED_MATCH_RATE_ENABLED = 'enhancedMatchRateEnabled',
  EXPORT_NAME = 'exportName',
  PERSONALIZATION_FIELDS = 'personalizationFields',
  END_DATE = 'endDate',
  SYNC_END = 'syncEnd',
  SCHEDULE = 'schedule',
  USER_CONSENT = 'userConsent', // General user consent for all platforms
  USER_DATA_CONSENT = 'dataConsent', // for Google and DV360
  USER_PERSONALIZATION_CONSENT = 'personalizationConsent', // for Google and DV360
  //field mappings
  VARIATION = 'variation',
  EMAIL_TYPE = 'email_type',
  AUDIENCE_EXPORT_CAMPAIGN_ID = 'audience_export_campaign_id',
  AUDIENCE_SEGMENT_ID = 'audience_segment_id',
  AUDIENCE_FOLDER = 'audience_folder',
  EMAIL_LIST_ID = 'email_list_id',
  EXPORT_FREQUENCY = 'export_frequency',
  MATCH_TYPE = 'matchType',
  API_TYPE = 'api_type',
  EXPORT_INCLUSION_CRITERIA = 'export_inclusion_criteria',
  ADDITIONAL_FIELDS = 'additionalFields',
  // Destination-specific
  REP_NAME = 'repName',
  CONTACT_LIST_NAME = 'contactListName',
  USE_TYPE = 'use_type',
  BILLING_GROUP_ID = 'billing_group_id',
  DESCRIPTION = 'description',
  TARGET_DELIVERY_DATE = 'target_delivery_date',
  SEND_DATE = 'send_date',
  VIANT_SEGMENT_NAME = 'viantSegmentName',
  VIANT_SEGMENT_ID = 'viantSegmentId',
  VIANT_SEGMENT_ID_TYPE = 'viantSegmentIdType',
  VIANT_AID = 'viantAid',
  ALL_PERSONALIZATION_FIELDS_SELECTED = 'all_personalization_fields_selected',
  ALL_GL_GENERATED_FIELDS_SELECTED = 'ALL_GL_GENERATED_FIELDS_SELECTED',
  CAMPAIGN_ID = 'campaign_id',
  CAMPAIGN_NAME = 'campaign_name',
  NEW_OR_EXISTING_CAMPAIGN = 'new_or_existing_campaign',
  MARKETING_CLOUD_BUSINESS_UNIT_ID = 'marketingCloudBusinessUnitId',
  MARKETING_CLOUD_FOLDER = 'marketingCloudFolder',
  PULSE_POINT_ADVERTISER = 'pulsePointAdvertiser',
  PULSE_POINT_PRODUCT = 'pulsePointProduct',
  QUALTRICS_LIST_TYPE = 'qualtricsListType',
  QUALTRICS_DIRECTORY_ID = 'qualtricsDirectoryId',
  MARKETO_AUDIENCE_TYPE = 'marketo_audience_type',
  MARKETO_CUSTOM_OBJECT = 'marketo_custom_object',
  DV360_APP_ID = 'dv360_app_id',
  GOOGLE_ADS_APP_ID = 'google_ads_app_id',
  AAM_DATA_SOURCE_ID = 'aam_data_source_id',
  AEP_DATASET_ID = 'aep_dataset_id',
  AEP_CUSTOM_NAMESPACE = 'aep_custom_namespace',
  TABLE_NAME = 'tableName',
  ACTIVATED_DESTINATIONS = 'distribution_manager_ids',
  // Sorting fields
  LIMIT = 'limit',
  SORT_BY = 'sortBy',
  SORT_DIRECTION = 'sortDirection',
  SELECTED_DATASET = 'selectedDataset',
}

export type AudienceExportSchedule = {
  schedule_type: CAMPAIGN_TYPE;
  frequency: Nullable<AudienceFrequencyEnum>;
  occurrence: Nullable<
    Array<{
      type: ByType.WEEK_DAY | ByType.MONTH_DAY;
      values: Weekday[] | number[];
      bySetPos?: Nullable<number[]>;
    }>
  >;
  start_type: AudienceScheduleStartTypeEnum;
  start_time: Nullable<{
    time: ISOString;
    timezone: string;
  }>;
  end_type: Nullable<AudienceScheduleEndTypeEnum>;
  end_time: Nullable<{
    time: ISOString;
    timezone: string;
  }>;
};

export type ExportConfigFormFieldTypeMap = {
  [ExportConfigFormField.PLATFORM_TYPE]: string;
  [ExportConfigFormField.LOSP]: string;
  [ExportConfigFormField.END_USER_OPT_IN]: boolean;
  [ExportConfigFormField.THIRD_PARTY_NAME]: string;
  [ExportConfigFormField.CLUB_SPONSOR_ADVERTISER_IO]: string;
  [ExportConfigFormField.OPT_IN_LANGUAGE_APPROVED_VETTED]: boolean;
  [ExportConfigFormField.CLUB_SPONSOR_ADVERTISER_DATA_SHARE]: string;
  [ExportConfigFormField.INFO_COLLECTED_VIA_MLB_PRIVACY_POLICY]: boolean;
  [ExportConfigFormField.CAMPAIGN_TYPE]: string;
  [ExportConfigFormField.SYNC_FREQUENCY]: string;
  [ExportConfigFormField.SYNC_END]: string;
  [ExportConfigFormField.USER_CONSENT]: string;
  [ExportConfigFormField.USER_DATA_CONSENT]: string;
  [ExportConfigFormField.USER_PERSONALIZATION_CONSENT]: string;
  [ExportConfigFormField.EXPORT_NAME]: string;
  [ExportConfigFormField.MAPPED_MATCH_FIELDS]: MappedMatchField[];
  [ExportConfigFormField.PERSONALIZATION_FIELDS]: Option[];
  [ExportConfigFormField.END_DATE]: string;
  [ExportConfigFormField.VARIATION]: string;
  [ExportConfigFormField.EMAIL_TYPE]: string;
  [ExportConfigFormField.AUDIENCE_EXPORT_CAMPAIGN_ID]: string;
  [ExportConfigFormField.AUDIENCE_SEGMENT_ID]: string;
  [ExportConfigFormField.EXPORT_FREQUENCY]: string;
  [ExportConfigFormField.EMAIL_LIST_ID]: string;
  [ExportConfigFormField.AUDIENCE_FOLDER]: string;
  [ExportConfigFormField.MATCH_TYPE]: Nullable<string>;
  [ExportConfigFormField.SCHEDULE]: Nullable<AudienceExportSchedule>;
  [ExportConfigFormField.API_TYPE]: Nullable<SailthruApiType>;
  [ExportConfigFormField.EXPORT_INCLUSION_CRITERIA]: Nullable<ExportInclusionCriteria>;
  [ExportConfigFormField.ENHANCED_MATCH_RATE_ENABLED]: boolean;
  [ExportConfigFormField.ADDITIONAL_FIELDS]: Record<
    string,
    IAdditionalFieldDTO
  >;
  [ExportConfigFormField.REP_NAME]: string;
  [ExportConfigFormField.CONTACT_LIST_NAME]: string;
  [ExportConfigFormField.USE_TYPE]: string;
  [ExportConfigFormField.SEND_DATE]: string;
  [ExportConfigFormField.TARGET_DELIVERY_DATE]: string;
  [ExportConfigFormField.BILLING_GROUP_ID]: string;
  [ExportConfigFormField.DESCRIPTION]: string;
  [ExportConfigFormField.VIANT_SEGMENT_NAME]: string;
  [ExportConfigFormField.VIANT_SEGMENT_ID]: string;
  [ExportConfigFormField.VIANT_SEGMENT_ID_TYPE]: string;
  [ExportConfigFormField.VIANT_AID]: string;
  [ExportConfigFormField.ALL_PERSONALIZATION_FIELDS_SELECTED]: boolean;
  [ExportConfigFormField.ALL_GL_GENERATED_FIELDS_SELECTED]: boolean;
  [ExportConfigFormField.CAMPAIGN_ID]: string;
  [ExportConfigFormField.CAMPAIGN_NAME]: string;
  [ExportConfigFormField.NEW_OR_EXISTING_CAMPAIGN]: string;
  [ExportConfigFormField.MARKETING_CLOUD_BUSINESS_UNIT_ID]: string;
  [ExportConfigFormField.MARKETING_CLOUD_FOLDER]: string;
  [ExportConfigFormField.PULSE_POINT_ADVERTISER]: string;
  [ExportConfigFormField.PULSE_POINT_PRODUCT]: string;
  [ExportConfigFormField.QUALTRICS_LIST_TYPE]: string;
  [ExportConfigFormField.QUALTRICS_DIRECTORY_ID]: string;
  [ExportConfigFormField.MARKETO_AUDIENCE_TYPE]: string;
  [ExportConfigFormField.MARKETO_CUSTOM_OBJECT]: string;
  [ExportConfigFormField.DV360_APP_ID]: string;
  [ExportConfigFormField.GOOGLE_ADS_APP_ID]: string;
  [ExportConfigFormField.AAM_DATA_SOURCE_ID]: string;
  [ExportConfigFormField.AEP_DATASET_ID]: string;
  [ExportConfigFormField.AEP_CUSTOM_NAMESPACE]: string;
  [ExportConfigFormField.TABLE_NAME]: string;
  [ExportConfigFormField.ACTIVATED_DESTINATIONS]: string[];
  // Sorting fields
  [ExportConfigFormField.LIMIT]: number;
  [ExportConfigFormField.SORT_BY]: string;
  [ExportConfigFormField.SORT_DIRECTION]: string;
  [ExportConfigFormField.SELECTED_DATASET]: number;
};

export type ExportConfigFormValues = {
  [K in ExportConfigFormField]: ExportConfigFormFieldTypeMap[K];
};

export type ExportFormValue =
  ExportConfigFormValues[keyof ExportConfigFormFieldTypeMap];

export enum ExportInclusionCriteria {
  DELTAS = 'deltas',
  SNAPSHOTS = 'snapshots',
}

export function generateDefaultConfig(): ExportConfigFormFieldTypeMap {
  return {
    [ExportConfigFormField.PLATFORM_TYPE]: '',
    [ExportConfigFormField.LOSP]: '',
    [ExportConfigFormField.END_USER_OPT_IN]: false,
    [ExportConfigFormField.THIRD_PARTY_NAME]: '',
    [ExportConfigFormField.CLUB_SPONSOR_ADVERTISER_IO]: '',
    [ExportConfigFormField.OPT_IN_LANGUAGE_APPROVED_VETTED]: false,
    [ExportConfigFormField.CLUB_SPONSOR_ADVERTISER_DATA_SHARE]: '',
    [ExportConfigFormField.INFO_COLLECTED_VIA_MLB_PRIVACY_POLICY]: false,
    [ExportConfigFormField.CAMPAIGN_TYPE]: '',
    [ExportConfigFormField.SYNC_FREQUENCY]: '',
    [ExportConfigFormField.SYNC_END]: '',
    [ExportConfigFormField.USER_CONSENT]: '',
    [ExportConfigFormField.USER_DATA_CONSENT]: '',
    [ExportConfigFormField.USER_PERSONALIZATION_CONSENT]: '',
    [ExportConfigFormField.EXPORT_NAME]: '',
    [ExportConfigFormField.MAPPED_MATCH_FIELDS]: [],
    [ExportConfigFormField.ENHANCED_MATCH_RATE_ENABLED]: false,
    [ExportConfigFormField.PERSONALIZATION_FIELDS]: [],
    [ExportConfigFormField.END_DATE]: '',
    [ExportConfigFormField.VARIATION]: '',
    [ExportConfigFormField.EMAIL_TYPE]: '',
    [ExportConfigFormField.AUDIENCE_EXPORT_CAMPAIGN_ID]: '',
    [ExportConfigFormField.AUDIENCE_SEGMENT_ID]: '',
    [ExportConfigFormField.EXPORT_FREQUENCY]: '',
    [ExportConfigFormField.EMAIL_LIST_ID]: '',
    [ExportConfigFormField.AUDIENCE_FOLDER]: '',
    [ExportConfigFormField.MATCH_TYPE]: null,
    [ExportConfigFormField.SCHEDULE]: null,
    [ExportConfigFormField.API_TYPE]: SailthruApiType.JOBS_API,
    [ExportConfigFormField.EXPORT_INCLUSION_CRITERIA]:
      ExportInclusionCriteria.DELTAS,
    [ExportConfigFormField.ADDITIONAL_FIELDS]: {},
    [ExportConfigFormField.REP_NAME]: '',
    [ExportConfigFormField.CONTACT_LIST_NAME]: '',
    [ExportConfigFormField.USE_TYPE]: '',
    [ExportConfigFormField.SEND_DATE]: '',
    [ExportConfigFormField.TARGET_DELIVERY_DATE]: '',
    [ExportConfigFormField.BILLING_GROUP_ID]: '',
    [ExportConfigFormField.DESCRIPTION]: '',
    [ExportConfigFormField.VIANT_SEGMENT_NAME]: '',
    [ExportConfigFormField.VIANT_SEGMENT_ID]: '',
    [ExportConfigFormField.VIANT_SEGMENT_ID_TYPE]: '',
    [ExportConfigFormField.VIANT_AID]: '',
    [ExportConfigFormField.ALL_PERSONALIZATION_FIELDS_SELECTED]: false,
    [ExportConfigFormField.ALL_GL_GENERATED_FIELDS_SELECTED]: false,
    [ExportConfigFormField.CAMPAIGN_ID]: '',
    [ExportConfigFormField.CAMPAIGN_NAME]: '',
    [ExportConfigFormField.NEW_OR_EXISTING_CAMPAIGN]: 'New',
    [ExportConfigFormField.MARKETING_CLOUD_BUSINESS_UNIT_ID]: '',
    [ExportConfigFormField.MARKETING_CLOUD_FOLDER]: '',
    [ExportConfigFormField.PULSE_POINT_ADVERTISER]: '',
    [ExportConfigFormField.PULSE_POINT_PRODUCT]: 'LIFE',
    [ExportConfigFormField.QUALTRICS_LIST_TYPE]: QualtricsListType.MAILING_LIST,
    [ExportConfigFormField.QUALTRICS_DIRECTORY_ID]: '',
    [ExportConfigFormField.MARKETO_AUDIENCE_TYPE]:
      MarketoAudienceType.STATIC_LIST,
    [ExportConfigFormField.MARKETO_CUSTOM_OBJECT]: '',
    [ExportConfigFormField.DV360_APP_ID]: '',
    [ExportConfigFormField.GOOGLE_ADS_APP_ID]: '',
    [ExportConfigFormField.AAM_DATA_SOURCE_ID]: '',
    [ExportConfigFormField.AEP_DATASET_ID]: '',
    [ExportConfigFormField.AEP_CUSTOM_NAMESPACE]: '',
    [ExportConfigFormField.TABLE_NAME]: '',
    [ExportConfigFormField.ACTIVATED_DESTINATIONS]: [],
    // Sorting fields
    [ExportConfigFormField.LIMIT]: 10,
    [ExportConfigFormField.SORT_BY]: 'created_at',
    [ExportConfigFormField.SORT_DIRECTION]: 'desc',
    [ExportConfigFormField.SELECTED_DATASET]: 0,
  };
}

export enum CAMPAIGN_TYPE {
  ONGOING = 'ongoing',
  ONE_TIME = 'single_export',
}

export enum UserDataConsent {
  CONSENT_STATUS_UNSPECIFIED = 'UNSPECIFIED',
  CONSENT_STATUS_GRANTED = 'GRANTED',
  CONSENT_STATUS_DENIED = 'DENIED',
}
export interface ExportErrorLogsResponse {
  data: ExportRunLog[];
  errors?: ApiError[];
  error_message?: string;
}
export interface ApiError {
  id: string;
  title: string;
}

export type ExportRunLog = {
  event: string;
  log_type?: string;
  timestamp?: string;
  audience_id?: string;
  batch_id?: string;
  platform?: string;
};
interface APIExportActivity {
  error_file_uri: string;
  batch_id: string;
  export_timestamp: string;
  platform: string;
  endpoint_id: string;
  supports_state: boolean;
  export_state: ExportState;
  time: string;
  added: number;
  removed: number;
  rejected: number;
  successfull_exports: number;
  failed_exports: number;
  audience_name: string;
  destination_name: string;
  export_run_id: ExportRunId;
}

export interface AudienceExportActivitiesResponse {
  export_activities: APIExportActivity[];
  error?: string;
}
export enum TABLE_STATES {
  INITIAL = 'initial',
  LOADING = 'loading',
  LOADED = 'loaded',
  ERROR = 'error',
}

export enum ExportStateUI {
  Queued = 'Queued',
  Processing = 'Processing',
  ProcessingFailed = 'Processing Failed',
  Exported = 'Exported',
  ExportFailed = 'Export Failed',
  ExportEmpty = 'Export Empty',
  NotEmpty = 'All except Empty', // This is a special state to pick all non-empty states
}

export interface ExportState {
  state: ExportStateUI;
  description: string;
}

export type AudienceExportActivity = {
  status: ExportState;
  export_time: string;
  added: number;
  removed: number;
  rejected: number;
  successful_records: number;
  rejected_records: number;
  export_id: ExportRunId;
  destination_name: string;
  error_file_uri: string;
  platform: string;
  audience_name: string;
};

export type ConsentType =
  | ConsentTypes.USER_CONSENT
  | ConsentTypes.USER_PERSONALIZATION_CONSENT
  | ConsentTypes.USER_DATA_CONSENT;

export enum ConsentTypes {
  USER_CONSENT = 'user_consent',
  USER_PERSONALIZATION_CONSENT = 'ad_personalization_consent',
  USER_DATA_CONSENT = 'user_data_consent',
}

export type MatchRatesResponse = {
  matchable: boolean;
  match_rates: Record<number, MatchRate>;
};

export type MatchRate = {
  value: string;
  text: string;
};
